import { useState, useEffect, useMemo } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useStepScreenshot } from "@/hooks/use-step-screenshot";
import {
  type HighlightTone,
  type ScreenshotHighlight,
  type TargetBox,
  isValidBox,
} from "./screenshot-types";
import { ScreenshotSvg } from "./screenshot-svg";

/** Standalone full-size zoomed screenshot view with bounding box and keyboard toggle */
export function ZoomedScreenshotView({
  walkthroughId,
  stepNumber,
  alt,
  targetBox,
  targetTone,
  highlights,
  activeHighlightId,
  onSelectHighlight,
  initialShowHighlights = true,
}: {
  walkthroughId: string;
  stepNumber: number;
  alt: string;
  targetBox?: TargetBox | null;
  targetTone?: HighlightTone | string | null;
  highlights?: ScreenshotHighlight[];
  activeHighlightId?: string | null;
  onSelectHighlight?: (id: string) => void;
  initialShowHighlights?: boolean;
}) {
  const { url, loading } = useStepScreenshot(walkthroughId, stepNumber);
  const [showHighlights, setShowHighlights] = useState(initialShowHighlights);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const activeHighlights = useMemo(() => {
    if (highlights && highlights.length > 0) return highlights;
    if (isValidBox(targetBox)) {
      return [{ id: "target", box: targetBox, tone: targetTone }];
    }
    return [];
  }, [highlights, targetBox, targetTone]);

  useEffect(() => {
    if (!url) return;
    const img = new Image();
    img.src = url;
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
  }, [url]);

  // Keyboard shortcut (H) to toggle highlights
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      if (e.key === "h" || e.key === "H") {
        setShowHighlights((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <Skeleton className="h-full w-full" />
      </div>
    );
  }
  if (!url) {
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <span className="text-sm text-muted-foreground">
          No screenshot available
        </span>
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full max-h-[82vh] flex-col items-center justify-center">
      {/* Top toolbar */}
      {activeHighlights.length > 0 && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5 rounded-md bg-background/90 px-2 py-1 text-xs font-medium shadow-xs backdrop-blur-xs">
          <button
            type="button"
            onClick={() => setShowHighlights((prev) => !prev)}
            className="inline-flex items-center gap-1 rounded bg-background/90 px-2 py-1 text-xs font-medium text-foreground shadow-xs backdrop-blur-xs transition-colors hover:bg-background focus-visible:outline-2 focus-visible:outline-ring"
            title={
              showHighlights ? "Hide annotations (H)" : "Show annotations (H)"
            }
            aria-label={
              showHighlights ? "Hide annotations" : "Show annotations"
            }
          >
            {showHighlights ? (
              <>
                <Eye className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                <span>Highlighted</span>
              </>
            ) : (
              <>
                <EyeOff
                  className="h-3.5 w-3.5 text-muted-foreground"
                  aria-hidden="true"
                />
                <span>Clean</span>
              </>
            )}
            <kbd className="hidden sm:inline-block ml-1 rounded border border-border bg-muted px-1 font-mono text-xs text-muted-foreground">
              H
            </kbd>
          </button>
        </div>
      )}

      <div className="relative flex h-full w-full max-h-[80vh] items-center justify-center overflow-hidden rounded">
        <ScreenshotSvg
          url={url}
          alt={alt}
          highlights={activeHighlights}
          activeHighlightId={activeHighlightId}
          showHighlights={showHighlights}
          onSelectHighlight={onSelectHighlight}
          naturalSize={naturalSize}
          className="h-full w-full max-h-[80vh] rounded object-contain shadow-xs"
        />
      </div>
    </div>
  );
}
